import { Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material'
import { ReactElement } from 'react'
import sectionImage from 'src/assets/success-factor-tool-kids.jpg'
import headerImage from 'src/assets/success-factors-public-header-illustration.svg'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { PrimaryButton } from 'src/shared/button/Buttons'
import { Footer } from 'src/shared/footer/Footer'
import { ArrowForwardIcon } from 'src/shared/icons/Icons'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { Section } from 'src/shared/presentation/Section'
import { useDelayedNavigate, useNavigateToLogin } from 'src/shared/utils/hooks/navigation-hooks'
import { Utils } from 'src/shared/utils/Utils'
import { useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'

const TextBlock = styled(Box)`
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }

  p {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`

const HeaderSectionStyled = styled(Box)`
  background: radial-gradient(89.91% 143.86% at 46.42% 24.08%, #ffffff 0%, #eef0f0 56.9%, #e6e6e6 100%);
  height: 37.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.divider};

  .MuiContainer-maxWidthLg {
    background: url(${headerImage}) no-repeat left bottom;
    background-size: contain;
    display: flex;
    align-items: center;
    justify-content: end;
    height: 100%;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: auto;
    padding: ${({ theme }) => theme.spacing(6)} 0;
    overflow: hidden;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: url(${headerImage}) no-repeat center bottom;
      background-size: contain;
      opacity: 0.3;
      pointer-events: none;
    }

    .MuiContainer-maxWidthLg {
      background: none;
      position: relative;
      justify-content: flex-start;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing(4)} 0;

    &::before {
      background-size: contain;
      opacity: 0.3;
    }
  }
`

const PageContentStyled = styled(Container)`
  margin: ${({ theme }) => theme.spacing(10)} auto ${({ theme }) => theme.spacing(12)};

  & > div {
    margin-bottom: ${({ theme }) => theme.spacing(7)};

    & > div:first-child {
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: ${({ theme }) => theme.spacing(6)} auto ${({ theme }) => theme.spacing(8)};

    & > div {
      margin-bottom: ${({ theme }) => theme.spacing(5)};
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: ${({ theme }) => theme.spacing(4)} auto ${({ theme }) => theme.spacing(6)};

    & > div {
      margin-bottom: ${({ theme }) => theme.spacing(4)};
    }
  }
`

const SectionCardContent = styled(CardContent)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: flex-start;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing(3)};
    padding: ${({ theme }) => theme.spacing(2)};

    &:last-child {
      padding-bottom: ${({ theme }) => theme.spacing(2)};
    }
  }
`

const MediaGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2)};

  .iframe-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    border-radius: ${({ theme }) => theme.spacing(1)};
  }

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.spacing(1)};
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: 1fr;

    img {
      max-height: 20rem;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    img {
      max-height: 14rem;
    }
  }
`

const PartnersGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    grid-template-columns: 1fr;
    padding: ${({ theme }) => theme.spacing(2)};
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`

const PartnerCard = styled('a')`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.spacing(1)};
  border: 1px solid ${({ theme }) => theme.colors.grey.divider};
  background-color: ${({ theme }) => theme.colors.common.white};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s ease-in-out;

  p {
    font-weight: 500;
    margin: 0;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
    background-color: ${({ theme }) => theme.colors.primary.light};
    box-shadow: ${({ theme }) => theme.shadows[4]};
    transform: translateY(-5px);

    p {
      font-weight: 600;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: ${({ theme }) => theme.spacing(2)};
  }
`

export const SuccessFactorsPublicLandingPage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()
  const login = useNavigateToLogin()
  const navigate = useDelayedNavigate()

  const handleViewAllSuccessFactors = () => {
    navigate(`/${ROUTES.SuccessFactorsPublicOverview.path}`)
  }

  const resolveGfsWebsiteLink = () => {
    if (locale === 'fr') {
      return 'https://promotionsante.ch/'
    } else if (locale === 'it') {
      return 'https://promozionesalute.ch/'
    } else return 'https://gesundheitsfoerderung.ch/'
  }

  return (
    <>
      <HeaderSectionStyled>
        <Container maxWidth="lg">
          <Stack spacing={3} maxWidth={{ xs: '100%', md: '26rem' }} ml={{ xs: 0, md: 'auto' }}>
            <Typography variant="display">{getMessage('label.success.factors.public.landing.page.title')}</Typography>
            <Typography color="text.secondary">
              {getMessage('label.success.factors.public.landing.page.main.description')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2}>
              <PrimaryButton
                onClick={handleViewAllSuccessFactors}
                messageKey={getMessage('button.view.all.success.factors')}
              />
              <Button variant="outlined" onClick={login} size={'large'}>
                {getMessage('button.login')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </HeaderSectionStyled>

      <PageContentStyled maxWidth="lg">
        <Section title={getMessage('label.success.factors.public.section.partners.title')}>
          <Card>
            <PartnersGrid>
              <PartnerCard
                href={`https://www.bag.admin.ch/bag/${locale}/home.html`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <HtmlRenderer
                  html={getMessage('label.success.factors.partners.bag')}
                  variant="body2"
                  color="textPrimary"
                />
              </PartnerCard>

              <PartnerCard href={resolveGfsWebsiteLink()} target="_blank" rel="noopener noreferrer">
                <HtmlRenderer
                  html={getMessage('label.success.factors.partners.gfs')}
                  variant="body2"
                  color="textPrimary"
                />
              </PartnerCard>

              <PartnerCard href={`https://www.tpf.admin.ch/${locale}`} target="_blank" rel="noopener noreferrer">
                <HtmlRenderer
                  html={getMessage('label.success.factors.partners.fpt')}
                  variant="body2"
                  color="textPrimary"
                />
              </PartnerCard>

              <PartnerCard href={`https://www.gdk-cds.ch/${locale}`} target="_blank" rel="noopener noreferrer">
                <HtmlRenderer
                  html={getMessage('label.success.factors.partners.gdk')}
                  variant="body2"
                  color="textPrimary"
                />
              </PartnerCard>
            </PartnersGrid>
          </Card>
        </Section>

        <Section title={getMessage('label.success.factors.public.section.1.title')}>
          <Card>
            <SectionCardContent>
              <Box>
                <MediaGrid>
                  <TextBlock>
                    <Typography variant="h3">
                      {getMessage('label.success.factors.public.section.1.background.subtitle')}
                    </Typography>
                    <Typography>{getMessage('label.success.factors.public.section.1.background.body.p1')}</Typography>
                    <br />
                    <HtmlRenderer html={getMessage('label.success.factors.public.section.1.background.body.p2')} />
                    <br />
                    <Typography>{getMessage('label.success.factors.public.section.1.background.body.p3')}</Typography>
                  </TextBlock>
                  <img src={sectionImage} alt="Senior man listening to music, running near a lake at the evening" />
                </MediaGrid>
              </Box>
              <TextBlock>
                <Typography variant="h3">
                  {getMessage('label.success.factors.public.section.1.aim.subtitle')}
                </Typography>
                <Typography>{getMessage('label.success.factors.public.section.1.aim.body.p1')}</Typography>
                <Typography>{getMessage('label.success.factors.public.section.1.aim.body.p2')}</Typography>
              </TextBlock>
              <PrimaryButton
                onClick={handleViewAllSuccessFactors}
                messageKey={getMessage('button.view.all.success.factors')}
                endIcon={<ArrowForwardIcon />}
              />
            </SectionCardContent>
          </Card>
        </Section>

        <Section title={getMessage('label.success.factors.public.section.2.title')}>
          <Card>
            <SectionCardContent>
              <Box>
                <MediaGrid>
                  <Stack spacing={5} alignItems="flex-start">
                    <TextBlock>
                      <Typography variant="h3">
                        {getMessage('label.success.factors.public.section.2.analysis.subtitle')}
                      </Typography>
                      <Typography>{getMessage('label.success.factors.public.section.2.analysis.body')}</Typography>
                    </TextBlock>
                    <TextBlock>
                      <Typography variant="h3">
                        {getMessage('label.success.factors.public.section.2.instructions.subtitle')}
                      </Typography>
                      <Typography>{getMessage('label.success.factors.public.section.2.instructions.body')}</Typography>
                    </TextBlock>
                  </Stack>
                  <Box className="iframe-container">
                    <iframe
                      src={Utils.adaptYouTubeUrlForEmbedding(getMessage('link.success.factors.public.instructions'))}
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </Box>
                </MediaGrid>
              </Box>
              <PrimaryButton messageKey={getMessage('button.login')} endIcon={<ArrowForwardIcon />} onClick={login} />
            </SectionCardContent>
          </Card>
        </Section>
      </PageContentStyled>

      <Footer />
    </>
  )
}
